import { pick, shake, sift } from 'radash';

import { SelectionService } from '@diagram/services/SelectionService';
import { SidebarService } from '@diagram/services/SidebarService';
import { ProductLinking, productLinkingState } from '@diagram/state/product-linking';
import { SidebarTab, SidebarType } from '@diagram/state/sidebar';

import { ComponentQuery, DiagramComponentInstance, hasMeasurementValue, PortQuery } from 'models';

import { getConnectedComponentInstances } from '../helpers/get-connected-component-instances';
import { ConnectionService } from './ConnectionService';
import { DiagramService } from './DiagramService';
import { PortService } from './PortService';

const ProductLinkingService = {
    setState: (state: ProductLinking) => {
        productLinkingState.componentInstance = state.componentInstance;
        productLinkingState.linkedComponentId = state.linkedComponentId;
        productLinkingState.defaultValues = state.defaultValues;
        productLinkingState.savedSearch = state.savedSearch;
    },

    getState: () => {
        return productLinkingState;
    },

    generateDefaultValues: (newComponentInstance: DiagramComponentInstance): Partial<ComponentQuery> => {
        const diagram = DiagramService.get();

        const configuration = newComponentInstance.configuration.ports;
        const specificationPorts = newComponentInstance.specifications.electrical.ports;
        const connections = ConnectionService.getConnectionsForComponentInstance(diagram, newComponentInstance);
        const componentHasConnections = connections.length > 0;

        const ports = specificationPorts.reduce((_ports: PortQuery[] = [], port, index) => {
            const portConfiguration = configuration[index];
            const voltageType = portConfiguration.voltageType;
            const portIsNotConfigured = !voltageType;

            const portIsNotConnected = () =>
                !PortService.isConnected({
                    componentInstanceId: newComponentInstance.id,
                    portKey: index,
                    connections: connections,
                });

            if (portIsNotConfigured || (componentHasConnections && portIsNotConnected())) {
                return _ports;
            }

            const next: PortQuery = {
                voltageType,

                //@ts-ignore
                ...shake(pick(port[voltageType], ['voltage', 'current', 'power']), (m) => !hasMeasurementValue(m)),
            };

            if (port.powerFlowDirection) {
                next.powerFlowDirection = port.powerFlowDirection;
            }

            if (portConfiguration.powerFlowDirection) {
                next.powerFlowDirection = portConfiguration.powerFlowDirection;
            }

            if ('isolated' in port && port.isolated) {
                next.isolated = true;
            }

            return [..._ports, next];
        }, []);

        const productsForCompatibility = sift(
            getConnectedComponentInstances(diagram, newComponentInstance.id, 3)
                .map((componentInstance) => componentInstance?.componentId)
                .filter(Boolean),
        );

        return {
            type: newComponentInstance.componentType,
            ports,
            productsForCompatibility,
            breakerType: getSpecifiedBreakerType(newComponentInstance),
        };
    },

    returnToComponentInstance: () => {
        const { componentInstance } = ProductLinkingService.getState();

        ProductLinkingService.stopLinking();

        if (componentInstance) {
            SelectionService.selectComponentInstance(componentInstance.id);
        }

        SidebarService.open(SidebarType.EDIT_COMPONENT);
    },

    stopLinking: () => {
        ProductLinkingService.setState({
            componentInstance: null,
            linkedComponentId: null,
        });
    },

    startLinking: (componentInstance: DiagramComponentInstance, prefillSpecifications = true) => {
        ProductLinkingService.setState({
            componentInstance,
            linkedComponentId: componentInstance.componentId,
            defaultValues: prefillSpecifications
                ? ProductLinkingService.generateDefaultValues(componentInstance)
                : { type: componentInstance.componentType },
        });

        SelectionService.selectComponentInstance(componentInstance.id);
        SidebarService.open(SidebarType.ADD, SidebarTab.ADD_CATALOG);
    },

    updateLinkedComponentId: (linkedComponentId: string | null) => {
        const oldState = ProductLinkingService.getState();

        ProductLinkingService.setState({ ...oldState, linkedComponentId });
    },
};

const getSpecifiedBreakerType = (componentInstance: DiagramComponentInstance): ComponentQuery['breakerType'] => {
    const electrical = componentInstance.specifications.electrical;

    return 'breakerType' in electrical && electrical.breakerType ? [electrical.breakerType] : undefined;
};

export { ProductLinkingService };
